import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../trpc";
import { getQueryKey } from "@trpc/react-query";
import type { AuthenticationsOutput } from "@zapier/bff";

export function useDeleteAuthentication() {
  const queryClient = useQueryClient();

  const authenticationsKey = getQueryKey(
    trpc.authentication.authentications,
    undefined,
    "any",
  );
  const connectionStatsKey = getQueryKey(
    trpc.authentication.connectionStats,
    undefined,
    "any",
  );

  const mutation = trpc.authentication.delete.useMutation({
    // Callback to handle optimistic updates in RQ cache
    onMutate: async ({ id }) => {
      if (!id) {
        return;
      }

      // Update Authentication under the authentications list key
      await queryClient.cancelQueries({ queryKey: authenticationsKey });

      const previousData = queryClient.getQueriesData<AuthenticationsOutput>({
        queryKey: authenticationsKey,
      });

      queryClient.setQueriesData(
        {
          queryKey: authenticationsKey,
          type: "active",
        },
        (old: AuthenticationsOutput | undefined) => {
          if (old?.results) {
            // Remove the deleted authentication from the list
            const updatedAuthenticationsArray = old?.results.filter(
              (item) => item.id !== id,
            );

            // this needs to return a new object ref for RQ to detect the change
            return {
              ...old,
              results: updatedAuthenticationsArray,
            };
          }

          return old;
        },
      );

      return { previousData };
    },
    // Restore the optimistic removal if the delete fails
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        for (const [key, data] of context.previousData) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    // Callback to handle cache invalidation after mutation is completed
    onSettled: () => {
      // Invalidate authentications key to refetch data
      queryClient.invalidateQueries({ queryKey: authenticationsKey });
      queryClient.invalidateQueries({ queryKey: connectionStatsKey });
    },
  });

  return {
    deleteAuthentication: mutation.mutate,
    isLoading: mutation.isPending,
  };
}
